import { Button } from "@blurb/codex-react";
import { Group, MantineProvider } from "@mantine/core";
import { GraphQL } from "@rpi/openapi-api";
import { IconV3 } from "@rpi/openapi-core";
import React from "react";
import { useOutlet } from "react-router-dom";
import { AppPage } from "../../components/AppPage";
import { RpiSearchFilter } from "../../components/search-filter/RpiSearchFilter";
import { SearchBar, SearchFilterBar } from "../../components/search-filter/SearchFilterBar";
import { SearchInput } from "../../configuration/filters/searchInput";
import { useCustomerProductFilters } from "../../configuration/filters/customerProduct.filter";
import { useAuthUser } from "../../hooks/useAuthUser";
import { useRpiPagination } from "../../hooks/usePagination";
import { rpaTheme } from "../../theme/rpaTheme";
import { SearchGeneral, SearchSort } from "../../utils/graphql.util";
import { CustomerProductCreateView, path as createPath } from "./CustomerProductCreateView";
import { PricingCalculator } from "../index/PricingCalculator";
import { CustomerProductsTable } from "./CustomerProductsTable";
import { useCustomerProducts } from "../../hooks/useCustomerProducts";
import { Pagination } from "../../components/Pagination";
import { CustomerProductFilter, path as filterPath } from "./CustomerProductFilter";
import { featureFlags } from "../../featureFlags";
import { CustomerProductViewV2 } from "./CustomerProductView";
import { useDrawerRoute } from "../../hooks/useDrawerRoute";
import { useNavigateRoute } from "../../hooks/useNavigateRoute";

export const CustomerProductsView: React.FC = () => {
  const user = useAuthUser();
  const navigateRoute = useNavigateRoute();
  const outlet = useOutlet();

  const createDrawerRoute = useDrawerRoute(createPath);
  const detailsDrawerRoute = useDrawerRoute("", /customer-products\/\d+\/?$/);
  const filterDrawerRoute = useDrawerRoute(filterPath);
  const editDrawerRoute = useDrawerRoute("", /\d+\/edit\/?$/);
  const [isPriceCalculatorOpen, setIsPriceCalculatorOpen] = React.useState(false);

  const sortInput: SearchSort<GraphQL.CustomerProductsQueryVariables> = {
    by: GraphQL.CustomerProductSearchField.Id,
    sortDirection: GraphQL.SortDirection.Desc,
  };
  const [searchInputs, setSearchInputs] = React.useState<SearchInput<GraphQL.CustomerProductSearchField>[]>([]);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.CustomerProductsQueryVariables>>();

  const pagination = useRpiPagination({ perPage: 20 });

  const customerProductFilters = useCustomerProductFilters();

  const { data, error, isLoading, refetch } = useCustomerProducts(user.is.SUPER_ADMIN, pagination, {
    inputs: searchInputs,
    sort: sortInput,
    generalInput: searchGeneral,
  });

  const customerProducts = data?.customerProducts.data;

  React.useEffect(() => {
    if (outlet === null) {
      refetch();
    }
  }, [outlet]);

  const [productId, setProductId] = React.useState<number | undefined>(undefined);

  const handleSearchSubmit = (searchValue: string) => {
    setSearchGeneral(searchValue || undefined);
  };

  return (
    <AppPage path={["Products"]} onReload={refetch}>
      <MantineProvider theme={rpaTheme}>
        <CustomerProductCreateView isOpened={createDrawerRoute.isOpen} onClose={createDrawerRoute.close} />
        <PricingCalculator
          isOpened={isPriceCalculatorOpen}
          onClose={() => setIsPriceCalculatorOpen(false)}
          productId={productId}
          customerProducts={customerProducts}
        />
      </MantineProvider>
      {featureFlags.newSearchExperience && user.is.SUPER_ADMIN && (
        <MantineProvider theme={rpaTheme}>
          <CustomerProductFilter
            isOpened={filterDrawerRoute.isOpen}
            onClose={filterDrawerRoute.close}
            filters={searchInputs}
            onApply={setSearchInputs}
          />
        </MantineProvider>
      )}
      {featureFlags.newProductDetails ? (
        <MantineProvider theme={rpaTheme}>
          <CustomerProductViewV2 isOpened={detailsDrawerRoute.isOpen} onClose={detailsDrawerRoute.close} />
        </MantineProvider>
      ) : (
        detailsDrawerRoute.isOpen && outlet
      )}
      {editDrawerRoute.isOpen && outlet}
      <AppPage.Section>
        <Group position={"apart"}>
          {featureFlags.newSearchExperience ? (
            user.is.SUPER_ADMIN ? (
              <SearchFilterBar
                placeholder="Search by Product Name or SKU"
                isLoading={isLoading}
                filters={searchInputs}
                onSetFilters={setSearchInputs}
                onSearchSubmit={handleSearchSubmit}
                onFilterClick={filterDrawerRoute.open}
              />
            ) : (
              <SearchBar
                placeholder="Search by Product Name or SKU"
                isLoading={isLoading}
                onSearchSubmit={handleSearchSubmit}
              />
            )
          ) : (
            <RpiSearchFilter
              placeholder="Search by Product Name or SKU"
              filters={customerProductFilters}
              onChange={setSearchInputs}
              onSearch={setSearchGeneral}
            />
          )}

          <MantineProvider theme={rpaTheme}>
            <Group spacing="sm">
              {user.is.SUPER_ADMIN && (
                <Button variant="filled" intent="primary" onClick={() => navigateRoute(["open-api-products"])}>
                  <Group spacing="xs">
                    <IconV3 icon="format_list_bulleted" color="inherit" size={20} /> Catalog
                  </Group>
                </Button>
              )}
              <Button
                variant="filled"
                intent="primary"
                onClick={createDrawerRoute.open}
                data-testid="addNewProductButton"
              >
                <Group spacing={"xs"}>
                  <IconV3 icon="add" color="inherit" /> Add New Product
                </Group>
              </Button>
            </Group>
          </MantineProvider>
        </Group>
        <MantineProvider theme={rpaTheme}>
          <CustomerProductsTable
            customerProducts={customerProducts}
            isLoading={isLoading}
            error={error}
            setIsPriceCalculatorOpen={setIsPriceCalculatorOpen}
            setProductId={setProductId}
            footer={<Pagination isLoading={isLoading} pagination={pagination} />}
          />
        </MantineProvider>
      </AppPage.Section>
    </AppPage>
  );
};
